// app/javascript/controllers/billing_charge_form_controller.js

/**
 * BillingChargeFormController
 *
 * Manages the interactive billing charge creation/editing form, providing real-time
 * calculations and smart field population to streamline marina billing workflows.
 *
 * Core Business Value:
 * - Eliminates manual calculations by automatically computing totals, taxes, and unit pricing
 * - Reduces data entry errors through service reference auto-population
 * - Provides flexible pricing models (unit-based vs. flat rate) for different service types
 * - Handles complex berth billing with automatic night calculations from date ranges
 *
 * Key Features:
 * - Service Reference Integration: Auto-populates description, tax rates, and unit pricing
 *   when marina staff selects from utilities, services, or berths
 * - Bi-directional Calculations: Users can edit any pricing field (unit price, total before tax,
 *   or total with tax) and other fields update automatically
 * - Smart Date Handling: For berth charges, calculates number of nights between check-in/out dates
 * - Tax Management: Automatically applies appropriate tax rates and displays formatted percentages
 * - Money Conversion: Integrates with money converter for proper cents/currency handling
 *
 * Form Integration:
 * Works with app/views/billing/charges/partials/_form.html.erb to provide a responsive
 * billing interface where marina staff can quickly create accurate charges without
 * manual calculation overhead.
 */

import { Controller } from "stimulus";
import Decimal from "decimal.js";
import "./money_converter_controller"; // Explicitly import money_converter_controller

// Configure Decimal.js for financial calculations
Decimal.set({
  precision: 20,
  rounding: Decimal.ROUND_HALF_UP,
});

export default class extends Controller {
  static targets = [
    "serviceReferenceSelect",
    "descriptionInput",
    "serviceStartDate",
    "serviceEndDate",
    "taxRateHidden",
    "taxRateDisplay",
    "amountAfterTaxInput",
    "amountInput",
    "unitNameInput",
    "unitAmountInput",
    "unitQuantityInput",
    "amountCentsHidden",
    "amountAfterTaxCentsHidden",
    "userIntendedTotalInput",
    "loaContainer",
    "loaInput",
    "priceSpinner",
  ];

  static values = {
    utilities: Object,
    services: Object,
    berths: Object,
    accountMarinaId: Number,
  };

  connect() {
    this.amountInputHasDirectEdit = false;
    this.totalWithTaxHasDirectEdit = false;

    // Pricing calculation state
    this.isCalculatingPrice = false;
    this.calculationTimeoutId = null;
    this.DEBOUNCE_DELAY = 400; // milliseconds

    // Clear any stale hint values on form load
    this.clearUserIntendedTotal();

    const initialTaxRate = parseFloat(this.taxRateHiddenTarget.value || 0);
    if (initialTaxRate > 0) {
      this.updateTaxRateDisplay(initialTaxRate);
    }

    // Check if this is an edit form with an existing user-entered total
    const existingTotal = parseFloat(this.amountAfterTaxInputTarget.value || 0);
    if (existingTotal > 0) {
      // Set the user intended total to preserve the existing value
      const totalCents = Math.round(existingTotal * 100);
      if (this.hasUserIntendedTotalInputTarget) {
        this.userIntendedTotalInputTarget.value = totalCents;
      }
      // Don't call setTotalAmounts() - preserve existing form values
      return;
    }

    // Initial calculation only for new charges
    this.setTotalAmounts();
  }

  // NEW: Clear user intended total when switching edit modes
  clearUserIntendedTotal() {
    if (this.hasUserIntendedTotalInputTarget) {
      this.userIntendedTotalInputTarget.value = "";
    }
  }

  showLoaField() {
    if (this.hasLoaContainerTarget) {
      this.loaContainerTarget.classList.remove("hidden");
    }
  }

  hideLoaField() {
    if (this.hasLoaContainerTarget) {
      this.loaContainerTarget.classList.add("hidden");
    }
  }

  shouldShowLoaField(serviceReferenceId) {
    // Only show LOA field for berth service references
    // The actual decision to show/hide will be made after API response indicates pricing strategy
    const selectedService = this.findServiceReferenceById(serviceReferenceId);
    return selectedService && selectedService.service_type === "Berth";
  }

  setFieldsFromServiceReference() {
    const selectedId = this.serviceReferenceSelectTarget.value;
    if (!selectedId) {
      this.hideLoaField();
      return;
    }

    // Find selected service reference data from all possible sources
    const selected = this.findServiceReferenceById(selectedId);
    if (!selected) return;

    // Update the form fields based on the selection
    this.descriptionInputTarget.value = selected.description || "";
    this.taxRateHiddenTarget.value = selected.tax_rate || 0;
    this.updateTaxRateDisplay(selected.tax_rate || 0);

    // Set unit fields from service reference
    this.setUnitFieldsFromServiceReference(selected);

    // For non-berth services: hide LOA field immediately since it's not needed
    // For berth services: show optimistically, API will hide if berth-based pricing
    if (this.shouldShowLoaField(selectedId)) {
      this.showLoaField();
    } else {
      this.hideLoaField();
    }

    // Update dates if service type is Berth
    if (selected.service_type === "Berth") {
      this.updateQuantityFromDates();
    }

    // Update calculations
    this.setTotalAmounts();

    // Trigger price calculation for berth services
    this.debouncedRecalculate();
  }

  findServiceReferenceById(id) {
    // Look in all possible service references and include service type info
    const utilities = this.utilitiesValue[id];
    const services = this.servicesValue[id];
    const berths = this.berthsValue[id];

    if (utilities) return { ...utilities, service_type: "Utility" };
    if (services) return { ...services, service_type: "Service" };
    if (berths) return { ...berths, service_type: "Berth" };

    return null;
  }

  updateTaxRateDisplay(taxRate) {
    if (this.hasTaxRateDisplayTarget) {
      const taxType = "VAT"; // Default to VAT
      const taxPercent = (taxRate * 100).toFixed(1);
      const formattedPercent = taxPercent.endsWith(".0") ? taxPercent.slice(0, -2) : taxPercent;
      this.taxRateDisplayTarget.value = `${taxType} ${formattedPercent}%`;
    }
  }

  setUnitFieldsFromServiceReference(selected) {
    if (!selected) return;

    // Auto-set unit type for berths
    if (selected.service_type === "Berth") {
      this.unitNameInputTarget.value = "day";
    } else {
      this.unitNameInputTarget.value = selected.unit_name || "service";
    }

    this.unitAmountInputTarget.value = (selected.unit_price_cents || 0) / 100;
    this.unitQuantityInputTarget.value = 1;

    this.dispatchMoneyConverterEvent("unit-price");
  }

  // Calculate unit quantity based on date range (for berth charges)
  updateQuantityFromDates() {
    // Only auto-calculate for berth charges with day unit type
    if (this.unitNameInputTarget.value !== "day") {
      return;
    }

    // Get both dates
    const startDate = this.constructor.getDateFromPicker(this.serviceStartDateTarget);
    const endDate = this.constructor.getDateFromPicker(this.serviceEndDateTarget);

    if (!startDate || !endDate) {
      return;
    }

    // Calculate number of nights (days difference)
    const nights = this.constructor.calculateNightsBetween(startDate, endDate);

    if (nights > 0) {
      this.unitQuantityInputTarget.value = nights;
      this.setTotalAmounts();
      this.debouncedRecalculate();
    }
  }

  // Helper to get date from flatpickr input
  static getDateFromPicker(dateInput) {
    // Try to get from flatpickr if available
    // eslint-disable-next-line no-underscore-dangle
    if (dateInput._flatpickr && dateInput._flatpickr.selectedDates.length > 0) {
      // eslint-disable-next-line no-underscore-dangle
      return dateInput._flatpickr.selectedDates[0];
    }

    // Otherwise try to parse the input value
    const dateValue = dateInput.value;
    return dateValue ? new Date(dateValue) : null;
  }

  // Calculate nights between two dates
  static calculateNightsBetween(startDate, endDate) {
    if (!startDate || !endDate) {
      return 0;
    }

    // Clone dates to avoid modifying original objects
    const start = new Date(startDate.getTime());
    const end = new Date(endDate.getTime());

    // Set times to midnight to ignore time part
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    // Calculate days difference
    const diffTime = end.getTime() - start.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return Math.max(0, diffDays);
  }

  recalculateFromLoa() {
    this.debouncedRecalculate();
  }

  debouncedRecalculate() {
    // Clear existing timeout
    if (this.calculationTimeoutId) {
      clearTimeout(this.calculationTimeoutId);
    }

    // Set new timeout
    this.calculationTimeoutId = setTimeout(() => {
      this.calculatePricing();
    }, this.DEBOUNCE_DELAY);
  }

  showLoadingStates() {
    this.isCalculatingPrice = true;

    try {
      this.priceSpinnerTargets.forEach((spinner) => {
        spinner.classList.remove("hidden");
      });
    } catch (error) {
      console.warn("Error showing loading spinners:", error);
    }
  }

  hideLoadingStates() {
    this.isCalculatingPrice = false;

    try {
      this.priceSpinnerTargets.forEach((spinner) => {
        spinner.classList.add("hidden");
      });
    } catch (error) {
      console.warn("Error hiding loading spinners:", error);
    }
  }

  async calculatePricing() {
    // Skip if already calculating or insufficient data
    if (this.isCalculatingPrice || !this.canCalculatePrice()) {
      return;
    }

    try {
      this.showLoadingStates();

      const pricingData = this.buildPricingRequest();
      const response = await this.callPricingAPI(pricingData);

      if (response.status === "success") {
        this.updateFormFieldsFromPricing(response.pricing);
      } else {
        console.warn("Pricing calculation failed:", response.message);
        // Silently fall back to manual entry - no user-facing error
      }
    } catch (error) {
      console.error("Pricing calculation error:", error);
      // Silently fall back to manual entry
    } finally {
      this.hideLoadingStates();
    }
  }

  canCalculatePrice() {
    const serviceRefId = this.serviceReferenceSelectTarget.value;
    const startDate = this.serviceStartDateTarget.value;
    const endDate = this.serviceEndDateTarget.value;

    if (!serviceRefId || !startDate || !endDate) {
      return false;
    }

    // Check if this is a berth service reference
    const selectedService = this.findServiceReferenceById(serviceRefId);
    return selectedService && selectedService.service_type === "Berth";
  }

  buildPricingRequest() {
    const serviceRefId = this.serviceReferenceSelectTarget.value;
    const startDate = this.serviceStartDateTarget.value;
    const endDate = this.serviceEndDateTarget.value;

    // Convert LOA from meters to centimeters
    let boatLoaCm = null;
    if (this.hasLoaInputTarget && this.loaInputTarget.value) {
      boatLoaCm = Math.round(parseFloat(this.loaInputTarget.value) * 100);
    }

    return {
      service_reference_id: parseInt(serviceRefId, 10),
      start_date: startDate,
      end_date: endDate,
      boat_loa_cm: boatLoaCm,
    };
  }

  async callPricingAPI(pricingData) {
    const { post } = await import("@rails/request.js");

    // Extract billable info from current URL or form data
    const billableId = this.constructor.getBillableId();
    const billableType = this.constructor.getBillableType();

    const url = `/billing/${billableType}/${billableId}/berth_pricing/calculate`;

    const response = await post(url, {
      body: pricingData,
      responseKind: "json",
    });

    if (response.ok) {
      return response.json;
    }
    throw new Error(`API request failed: ${response.status}`);
  }

  static getBillableId() {
    // Extract from current URL path (handles nested paths like /admin/billing/booking/123)
    const path = window.location.pathname;
    const matches = path.match(/\/billing\/[^/]+\/(\d+)/);
    return matches ? matches[1] : null;
  }

  static getBillableType() {
    // Extract from current URL path (handles nested paths like /admin/billing/booking/123)
    const path = window.location.pathname;
    const matches = path.match(/\/billing\/([^/]+)\/\d+/);
    return matches ? matches[1] : "booking";
  }

  updateFormFieldsFromPricing(pricing) {
    // Show/hide LOA field based on pricing strategy
    if (pricing.strategy === "loa_based") {
      this.showLoaField();
    } else {
      this.hideLoaField();
    }

    // Update unit amount (daily rate from seasonal breakdown if available)
    if (pricing.seasonal_breakdown
        && pricing.seasonal_breakdown.length > 0
        && pricing.total_nights > 0) {
      const avgDailyRate = pricing.net_total_cents / (pricing.total_nights * 100);
      this.unitAmountInputTarget.value = avgDailyRate.toFixed(2);
      this.dispatchMoneyConverterEvent("unit-price");
    }

    // Update unit quantity (total nights)
    if (pricing.total_nights) {
      this.unitQuantityInputTarget.value = pricing.total_nights;
    }

    // Update net total
    if (pricing.net_total_cents) {
      const netTotal = pricing.net_total_cents / 100;
      this.amountInputTarget.value = netTotal.toFixed(2);
      this.amountCentsHiddenTarget.value = pricing.net_total_cents;
      this.dispatchMoneyConverterEvent("price-without-tax");
    }

    // Update gross total
    if (pricing.gross_total_cents) {
      const grossTotal = pricing.gross_total_cents / 100;
      this.amountAfterTaxInputTarget.value = grossTotal.toFixed(2);
      this.amountAfterTaxCentsHiddenTarget.value = pricing.gross_total_cents;
      this.dispatchMoneyConverterEvent("price-with-tax");
    }

    // Clear any user intended total since we're auto-calculating
    this.clearUserIntendedTotal();
  }

  setTotalAmountAfterTax() {
    if (this.totalWithTaxHasDirectEdit) {
      return;
    }

    const baseAmount = new Decimal(this.amountInputTarget.value || 0);
    const taxRate = new Decimal(this.taxRateHiddenTarget.value || 0);

    if (baseAmount.isZero()) {
      this.amountAfterTaxInputTarget.value = "";
      this.amountAfterTaxCentsHiddenTarget.value = "";
    } else {
      // Use SAME calculation as backend: amount * (1 + tax_rate)
      const baseAmountCents = baseAmount.mul(100).round();
      const totalCents = baseAmountCents.mul(Decimal.add(1, taxRate)).round();

      this.amountAfterTaxInputTarget.value = totalCents.div(100).toFixed(2);
      this.amountAfterTaxCentsHiddenTarget.value = totalCents.toString();

      this.dispatchMoneyConverterEvent("price-with-tax");
    }
  }

  setPriceFromTotalWithTax() {
    const totalWithTax = new Decimal(this.amountAfterTaxInputTarget.value || 0);
    const taxRate = new Decimal(this.taxRateHiddenTarget.value || 0);

    if (
      totalWithTax.isZero()
      || taxRate.isNaN()
      || !totalWithTax.isFinite()
      || !taxRate.isFinite()
    ) {
      this.clearUserIntendedTotal();
      return;
    }

    this.totalWithTaxHasDirectEdit = true;

    // Store user's intended total for backend processing
    const targetTotalCents = totalWithTax.mul(100)
      .round()
      .toNumber();
    if (this.hasUserIntendedTotalInputTarget) {
      this.userIntendedTotalInputTarget.value = targetTotalCents;
    }

    // Calculate optimal pre-tax amount for display
    const taxRateNum = taxRate.toNumber();
    let exactPreTaxCents = Math.floor(targetTotalCents / (1 + taxRateNum));

    // Test for exact match (for display purposes only - backend will recalculate)
    for (let offset = 0; offset <= 2; offset += 1) {
      const testPreTax = exactPreTaxCents + offset;
      const calculatedTotal = Math.round(testPreTax * (1 + taxRateNum));

      if (calculatedTotal === targetTotalCents) {
        exactPreTaxCents = testPreTax;
        break;
      }
    }

    // Update form fields for display
    this.amountInputTarget.value = (exactPreTaxCents / 100).toFixed(2);
    this.amountCentsHiddenTarget.value = exactPreTaxCents;
    this.amountAfterTaxCentsHiddenTarget.value = targetTotalCents;

    // Update unit price
    const quantity = new Decimal(this.unitQuantityInputTarget.value || 1);
    if (quantity.gt(0)) {
      const unitPrice = new Decimal(exactPreTaxCents).div(100).div(quantity);
      this.unitAmountInputTarget.value = unitPrice.toFixed(2);
      this.dispatchMoneyConverterEvent("unit-price");
    }

    this.dispatchMoneyConverterEvent("price-without-tax");
  }

  setTotalAmounts() {
    if (!this.amountInputHasDirectEdit && !this.totalWithTaxHasDirectEdit) {
      // Check if this charge already has a user-entered total and preserve it
      const currentTotal = parseFloat(this.amountAfterTaxInputTarget.value || 0);
      const hasUserEnteredTotal = currentTotal > 0 && this.hasUserIntendedTotalInputTarget
                                 && this.userIntendedTotalInputTarget.value;

      if (hasUserEnteredTotal) {
        // Don't recalculate - preserve the existing user-entered total
        return;
      }

      // Clear hint since we're calculating from unit amounts
      this.clearUserIntendedTotal();

      const unitPrice = new Decimal(this.unitAmountInputTarget.value || 0);
      const quantity = new Decimal(this.unitQuantityInputTarget.value || 1);

      // Calculate using Decimal for precision
      const calculatedAmount = unitPrice.mul(quantity);

      const formattedAmount = calculatedAmount.toFixed(2);
      if (this.amountInputTarget.value !== formattedAmount) {
        this.amountInputTarget.value = formattedAmount;
        this.dispatchMoneyConverterEvent("price-without-tax");
      }
    }

    this.amountInputHasDirectEdit = false;
    this.totalWithTaxHasDirectEdit = false;
    this.setTotalAmountAfterTax();
  }

  // NEW: Clear hint when switching back to net amount editing
  setUnitPriceFromAmount() {
    // Clear the user intended total since they're now editing net amount
    this.clearUserIntendedTotal();

    const amount = new Decimal(this.amountInputTarget.value || 0);
    const quantity = new Decimal(this.unitQuantityInputTarget.value || 1);

    if (amount.isZero() || quantity.isZero()) {
      return;
    }

    this.amountInputHasDirectEdit = true;

    // Calculate unit price using Decimal
    const unitPrice = amount.div(quantity);
    this.unitAmountInputTarget.value = unitPrice.toFixed(2);

    this.dispatchMoneyConverterEvent("unit-price");
    this.setTotalAmountAfterTax();
  }

  dispatchMoneyConverterEvent(eventId) {
    const event = new CustomEvent(`money-converter:${eventId}-changed`, { bubbles: true });
    this.element.dispatchEvent(event);
  }

  disconnect() {
    // Clear any pending timeout
    if (this.calculationTimeoutId) {
      clearTimeout(this.calculationTimeoutId);
      this.calculationTimeoutId = null;
    }
  }
}
